import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, BehaviorSubject, ReplaySubject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { selectors as coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { AuthService } from 'app/services/auth.service';
import { ModalSimpleMessageService } from 'app/shared/modals/modal-simple-message.service';
import { Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import User from 'entity/User';
import { SocialEventType, Presentation, Presenter, SocialState } from 'messages/websocket.messages';

import { Player } from '../social-pixi-canvas/player';
import SocialStateHelper from '../social-state-machine/social-state-helper';
import SocialStateMachine from '../social-state-machine/social-state-machine';

import { PresentationService } from './presentation/presentation.service';
import { SceneService } from './scene.service';

@Injectable({
  providedIn: 'root',
})
export class PerformerViewService extends Loggable implements OnDestroy {
  private sceneService = inject(SceneService);
  private authService = inject(AuthService);
  private modalService = inject(ModalSimpleMessageService);
  private presentationService = inject(PresentationService);
  private store = inject(Store);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private inviteShown = false;
  private isPerformingSource = new BehaviorSubject<boolean>(false);
  private isPresenting = false;
  private presenters: string[] = [];
  private queue: Player[] = [];
  private queueInterval?: Timeout;
  private readonly CROWD_SIZE = 20;
  private user?: User;

  public audience: Player[] = [];
  public isPerforming = this.isPerformingSource.asObservable();
  public shouldRefreshUserVideo$: Subject<string> = new Subject<string>();

  private audienceSubject = new BehaviorSubject<Player[]>([]);
  public audience$ = this.audienceSubject.asObservable();

  //Flags
  public presentationV3 = false;

  public constructor() {
    super();
    this.setDisplayName('PerformerViewService');
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((user) => (this.user = user));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_v3) => {
        this.presentationV3 = presentation_v3;
      });

    this.listenToState();
  }

  private listenToState(): void {
    SocialStateMachine.shared.stateChanged$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: async (state) => {
        assertDefined(this.user?.userId);
        this.presenters = SocialStateHelper.getPresenterIds(state);
        const socialEvent = state.socialEvent as Presentation;
        if (this.shouldClose(state)) {
          this.close();
          return;
        }

        if (this.nobodyPresenting(socialEvent)) {
          this.warn('Presentation active but no active or invited presenters', {
            socialEvent,
            prevPresenters: this.presenters,
          });
          this.close();
        }

        if (this.shouldStartPresentation(state)) {
          await this.requestAcceptPresenterInvite();
          this.start();
          return;
        }

        if (this.shouldShowInvite(state)) {
          this.showInviteDialog(socialEvent.presentationCreator);
          return;
        } else if (!SocialStateHelper.isUserInvited(state, this.user.userId)) {
          this.modalService.close();
          this.inviteShown = false;
        }
      },
      error: (e: unknown) => this.error('Error while subscribing to state change', { e }),
    });
  }

  private async requestAcceptPresenterInvite(): Promise<void> {
    const presenterData = this.getPresenterData();
    await SocialStateMachine.shared
      .requestAcceptPresenterInvite(presenterData)
      .catch((e) => this.error('Error request accept presenter invite', e));
  }

  private getPresenterData(): { presenter: Presenter; userId: string } {
    assertDefined(this.user?.userId);
    const { firstName, lastName, userId } = this.user;
    const isGuest = this.authService.isAnonymous();
    const presenter: Presenter = { userId, firstName, lastName, isGuest };
    return { presenter, userId };
  }

  private shouldClose(state: SocialState): boolean {
    assertDefined(this.user?.userId);

    return (
      !!this.isPresenting &&
      (!SocialStateHelper.isEventHappening(state, SocialEventType.PRESENTATION) ||
        !SocialStateHelper.isUserPresenter(state, this.user.userId))
    );
  }

  private nobodyPresenting({ presenters, requestedPresenterIds }: Presentation): boolean {
    return (
      presenters && requestedPresenterIds && requestedPresenterIds.length === 0 && Object.keys(presenters).length === 0
    );
  }

  private shouldShowInvite(state: SocialState): boolean {
    assertDefined(this.user?.userId);
    return (
      SocialStateHelper.isEventHappening(state, SocialEventType.PRESENTATION) &&
      !this.isPresenting &&
      !this.inviteShown &&
      SocialStateHelper.isUserInvited(state, this.user.userId)
    );
  }

  private shouldStartPresentation(state: SocialState): boolean {
    assertDefined(this.user?.userId);
    return (
      SocialStateHelper.isEventHappening(state, SocialEventType.PRESENTATION) &&
      !this.isPresenting &&
      SocialStateHelper.isUserPresenter(state, this.user.userId)
    );
  }

  private start(): void {
    if (this.isPresenting) {
      return;
    }
    // Set flags
    this.isPresenting = true;
    this.isPerformingSource.next(true);
    if (!this.presentationV3) {
      // Hide Scene if isn't new transition
      this.sceneService.hidePlayerWorld();
    }

    this.prepareAudience();
  }

  public async endOwnPresentation(): Promise<void> {
    await this.presentationService.stopPresenting().catch((e) => this.error('unable to end own presentation', e));
    this.close();
  }

  private prepareAudience(): void {
    this.setAudienceAndQueue();
    this.queueInterval = setInterval(() => {
      this.setAudienceAndQueue();
      if (this.queue?.length > 0) {
        const randomAudiencePosition: number = Math.floor(Math.random() * this.CROWD_SIZE);
        const randomQueuePosition: number = Math.floor(Math.random() * this.queue.length);
        const queueParticipant: Player = this.queue[randomQueuePosition];
        this.queue[randomQueuePosition] = this.audience[randomAudiencePosition];
        this.audience[randomAudiencePosition] = queueParticipant;
      }
    }, 10 * 1000);
  }

  public setAudienceAndQueue(): void {
    const players = this.shuffledPlayers();
    const filtered = players.filter((p) => !this.presenters.includes(p.userId));
    this.audience = [...filtered].splice(0, this.CROWD_SIZE);
    this.queue = [...filtered].splice(this.CROWD_SIZE, filtered.length - this.CROWD_SIZE);
    this.audienceSubject.next(this.audience);
  }

  public refreshUserVideo(userId: string): void {
    this.shouldRefreshUserVideo$.next(userId);
  }

  public setAudience(): void {
    // Refresh the players from the sceneService.sceneManager main list. The player may have been removed, so ensure
    // that the refreshed player is not undefined
    this.audience = this.audience
      .map(({ userId }) => this.sceneService.sceneManager?.getParticipant(userId))
      .filter(notNil);
    this.audienceSubject.next(this.audience);
  }

  public removeParticipant(removeSid: string): void {
    for (const i in this.audience) {
      if (this.audience[i].userId === removeSid) {
        if (this.queue.length > 0) {
          const randomQueuePosition: number = Math.floor(Math.random() * this.queue.length);
          this.audience[i] = this.queue[randomQueuePosition];
          this.queue.splice(randomQueuePosition, 1);
        } else {
          this.audience.splice(Number(i), 1);
        }
      }
    }

    // Remove from queue
    for (const i in this.queue) {
      const connectionId = this.queue[i].userId;
      if (connectionId === removeSid) {
        this.audience.splice(Number(i), 1);
      }
    }

    setTimeout(() => {
      this.setAudience();
    }, 1200);
  }

  private close(): void {
    if (this.queueInterval) {
      clearInterval(this.queueInterval);
    }

    if (this.isPresenting) {
      this.isPerformingSource.next(false);
      this.sceneService.showPlayerWorld();
    }

    this.audience = [];
    this.isPresenting = false;
    this.inviteShown = false;
  }

  private shuffledPlayers(): Player[] {
    const players = this.sceneService.sceneManager?.participantsList;
    if (!players) {
      return [];
    }
    return players.sort(() => Math.random() - 0.5);
  }

  private showInviteDialog(presentationCreatorId: string): void {
    this.inviteShown = true;
    const presentationCreator = this.sceneService.sceneManager?.getParticipant(presentationCreatorId);
    const {
      user: { firstName, lastName },
    } = presentationCreator ?? { user: {} };
    let name = `${firstName ?? ''} ${lastName ?? ''}`;

    if (name.trim() === '') {
      name = 'A host';
    }
    const message = `${name} has invited you to present for the members of this Room.`;
    this.modalService
      .confirm({
        title: '',
        message,
        actionButtonTitle: 'Join presentation',
        customModalClass: 'modal-inline modal-theme-success',
        showDoneButton: true,
      })
      .subscribe({
        next: async (res) => {
          if (res) {
            await this.presentationService
              .joinPresentation()
              .catch((e) => this.error('failed ot join presentation', e));
          } else {
            const presenterData = this.getPresenterData();
            await SocialStateMachine.shared
              .requestRejectPresenterInvite(presenterData)
              .catch((e) => this.error('Error reject presenter invite', e));
            this.inviteShown = false;
          }
        },
        error: (e: unknown) => this.error('Error show inviteInviteDialog', { e }),
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
